<template>
  <div
    class="input-v2"
    :class="{
      'input-v2--error': error,
      'input-v2--disabled': disabled,
    }"
    :dir="computedDir"
  >
    <label v-if="label" class="input-v2__label">
      {{ label }}<span v-if="required" class="input-v2__required">&nbsp;*</span>
    </label>

    <div class="input-v2__field">
      <slot name="prefix" />
      <input
        ref="input"
        class="input-v2__control"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="inputListeners"
      />
      <slot name="suffix" />
    </div>

    <p
      v-if="error && errorMessage"
      class="input-v2__hint input-v2__hint--error"
    >
      {{ errorMessage }}
    </p>
    <p v-else-if="hint" class="input-v2__hint">{{ hint }}</p>
  </div>
</template>

<script>
export default {
  name: 'InputV2',
  inheritAttrs: false,
  model: { prop: 'value', event: 'input' },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dir: {
      type: String,
      default: '',
    },
  },
  computed: {
    computedDir() {
      if (this.dir) return this.dir;
      const rtlLocales = ['he', 'ar', 'fa', 'ur', 'ur_IN'];
      return this.$i18n && rtlLocales.includes(this.$i18n.locale)
        ? 'rtl'
        : 'ltr';
    },
    inputListeners() {
      return {
        ...this.$listeners,
        input: event => this.$emit('input', event.target.value),
      };
    },
  },
  methods: {
    focus() {
      if (this.$refs.input) this.$refs.input.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.input-v2 {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.input-v2__label {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  color: theme('colors.gray.700');
}

.input-v2__required {
  color: theme('colors.brand.DEFAULT');
}

.input-v2__field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  border: 1px solid theme('colors.gray.300');
  border-radius: 8px;
  background: theme('colors.white');
  overflow: hidden;
  transition: border-color 0.15s ease;

  &:hover,
  &:focus-within {
    border-color: theme('colors.gray.400');
  }
}

.input-v2__control {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0 12px;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  font-family: 'Open Sans', sans-serif;
  color: theme('colors.gray.800');

  &:hover,
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: theme('colors.gray.400');
    opacity: 1;
  }

  &:disabled {
    background: transparent;
    cursor: not-allowed;
  }
}

.input-v2__hint {
  font-size: 14px;
  line-height: 20px;
  font-family: 'Open Sans', sans-serif;
  color: theme('colors.gray.700');
}

.input-v2--error {
  .input-v2__label {
    color: theme('colors.red.700');
  }

  .input-v2__field {
    border-color: theme('colors.red.700');

    &:hover,
    &:focus-within {
      border-color: theme('colors.red.700');
    }
  }

  .input-v2__hint--error {
    color: theme('colors.red.700');
  }
}

.input-v2--disabled {
  .input-v2__label {
    color: theme('colors.neutral.500');
  }

  .input-v2__field {
    background: theme('colors.neutral.100');
    border-color: theme('colors.neutral.300');

    &:hover,
    &:focus-within {
      border-color: theme('colors.neutral.300');
    }
  }

  .input-v2__control {
    color: theme('colors.neutral.300');

    &::placeholder {
      color: theme('colors.neutral.300');
    }
  }
}
</style>
